import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableFooter,
  Paper,
  Checkbox,
  Typography,
  Box,
  Chip,
  Tooltip,
} from "@material-ui/core";
import PaymentIcon from "@material-ui/icons/Payment";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { formatearFecha } from "src/utils/formatTime";
import { formatAsCLP } from "src/utils/formatNumber";
import { makeStyles } from "@material-ui/styles";

const useStyles = makeStyles((theme) => ({
  headerCell: {
    fontWeight: 700,
    backgroundColor: theme.palette.grey[100],
  },
  footerRow: {
    backgroundColor: theme.palette.grey[50],
  },
  totalLabel: {
    fontWeight: 700,
    fontSize: "0.95rem",
  },
  totalAmount: {
    fontWeight: 700,
    fontSize: "1rem",
    color: theme.palette.primary.main,
  },
  chipTarea: {
    backgroundColor: "#E3F2FD",
    color: "#1565C0",
    fontWeight: 600,
    height: 22,
    fontSize: "0.7rem",
  },
  chipPlan: {
    backgroundColor: "#E8F5E9",
    color: "#2E7D32",
    fontWeight: 600,
    height: 22,
    fontSize: "0.7rem",
  },
  chipVencida: {
    backgroundColor: "#FFEBEE",
    color: "#C62828",
    fontWeight: 600,
    height: 22,
    fontSize: "0.7rem",
  },
  selectedCount: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  payButton: {
    minWidth: 180,
  },
}));

export default function FacturasPendientesPagoDialog({
  open,
  setOpen,
  facturasPendientes,
}) {
  const classes = useStyles();
  const navigate = useNavigate();

  // ── Estado de selección ──────────────────────────────────────────────────
  const [seleccionadas, setSeleccionadas] = useState([]);

  // Calcular monto total de las facturas seleccionadas (usa montoRestante cuando hay abono)
  const montoTotal = useMemo(
    () =>
      seleccionadas.reduce(
        (acc, f) => acc + Number(f.montoRestante ?? f.total),
        0,
      ),
    [seleccionadas],
  );

  // ── Handlers de selección ────────────────────────────────────────────────
  const handleToggle = (factura) => {
    setSeleccionadas((prev) => {
      const yaSeleccionada = prev.some((f) => f.folio === factura.folio);
      return yaSeleccionada
        ? prev.filter((f) => f.folio !== factura.folio)
        : [...prev, factura];
    });
  };

  const handleToggleAll = () => {
    setSeleccionadas((prev) =>
      prev.length === facturasPendientes.length ? [] : [...facturasPendientes],
    );
  };

  const estaSeleccionada = (factura) =>
    seleccionadas.some((f) => f.folio === factura.folio);

  const todasSeleccionadas =
    facturasPendientes.length > 0 &&
    seleccionadas.length === facturasPendientes.length;

  const algunaSeleccionada = seleccionadas.length > 0 && !todasSeleccionadas;

  // ── Handler de pago ──────────────────────────────────────────────────────
  const handlePagar = () => {
    if (seleccionadas.length === 0) return;

    // Normalizar tipoFactura a mayúsculas para el backend
    const facturasNormalizadas = seleccionadas.map((f) => ({
      tipoFactura: f.tipo === "plan" ? "PLAN" : "TAREA",
      idFactura: f.id,
      idTarea: f.tipo === "tarea" ? f.refTarea : undefined,
      monto: Number(f.montoRestante ?? f.total),
      sucursal: f.sucursal ?? "",
      folio: f.folio,
    }));

    localStorage.setItem("lookBills", JSON.stringify(false));
    setOpen(false);

    if (seleccionadas.length === 1) {
      // Pago individual — flujo existente de Fase 6
      const factura = facturasNormalizadas[0];
      const sufijo = factura.tipoFactura === "PLAN" ? "0" : "1";
      navigate(`/dashboard/pago/${factura.idFactura}_${sufijo}`);
    } else {
      // Pago múltiple — Fase 7
      navigate("/dashboard/pago/multiple", {
        state: {
          facturas: facturasNormalizadas,
          montoTotal: montoTotal,
        },
      });
    }
  };

  // ── Cerrar sin pagar ─────────────────────────────────────────────────────
  const handleCerrar = () => {
    localStorage.setItem("lookBills", JSON.stringify(false));
    setSeleccionadas([]);
    setOpen(false);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      aria-labelledby="facturas-pendientes-title"
    >
      <DialogTitle id="facturas-pendientes-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>Facturas Pendientes de Pago</span>
          {seleccionadas.length > 0 && (
            <Typography className={classes.selectedCount}>
              {seleccionadas.length} seleccionada
              {seleccionadas.length !== 1 ? "s" : ""} —{" "}
              <strong>{formatAsCLP(montoTotal)}</strong>
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" className={classes.headerCell}>
                  <Tooltip
                    title={
                      todasSeleccionadas
                        ? "Deseleccionar todas"
                        : "Seleccionar todas"
                    }
                  >
                    <Checkbox
                      indeterminate={algunaSeleccionada}
                      checked={todasSeleccionadas}
                      onChange={handleToggleAll}
                      color="primary"
                    />
                  </Tooltip>
                </TableCell>
                <TableCell className={classes.headerCell}>Folio</TableCell>
                <TableCell className={classes.headerCell}>Tipo</TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Monto a pagar
                </TableCell>
                <TableCell className={classes.headerCell}>
                  Fecha Facturación
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {facturasPendientes.map((row, idx) => (
                <TableRow
                  key={`factura-pendiente-${idx}`}
                  hover
                  selected={estaSeleccionada(row)}
                  onClick={() => handleToggle(row)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={estaSeleccionada(row)}
                      color="primary"
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggle(row)}
                    />
                  </TableCell>
                  <TableCell>{row.folio}</TableCell>
                  <TableCell>
                    <Box
                      display="flex"
                      gap={0.5}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Chip
                        label={row.tipo === "plan" ? "Plan" : "Tarea"}
                        className={
                          row.tipo === "plan"
                            ? classes.chipPlan
                            : classes.chipTarea
                        }
                        size="small"
                      />
                      {row.vencida === 1 && (
                        <Chip
                          label="Vencida"
                          className={classes.chipVencida}
                          size="small"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {row.estadoFactura === "ABONO" &&
                    row.montoRestante != null ? (
                      <Tooltip
                        title={`Total factura: ${formatAsCLP(row.total)} · Abono previo: ${formatAsCLP(row.total - row.montoRestante)}`}
                        arrow
                      >
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          style={{ gap: 4, cursor: "help" }}
                        >
                          <Typography
                            variant="body2"
                            style={{ color: "#f59e0b", fontWeight: 600 }}
                          >
                            {formatAsCLP(row.montoRestante)}
                          </Typography>
                          <InfoOutlinedIcon
                            style={{ fontSize: 14, color: "#f59e0b" }}
                          />
                        </Box>
                      </Tooltip>
                    ) : (
                      formatAsCLP(Number(row.total))
                    )}
                  </TableCell>
                  <TableCell>
                    {row.fechaFacturacion
                      ? formatearFecha(
                          row.fechaFacturacion,
                          "DD/MM/YYYY",
                          "YYYY/MM/DD",
                        )
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            {/* Footer con total general de todas las facturas pendientes */}
            <TableFooter>
              <TableRow className={classes.footerRow}>
                <TableCell colSpan={3} />
                <TableCell align="right" className={classes.totalLabel}>
                  {formatAsCLP(
                    facturasPendientes.reduce(
                      (acc, f) => acc + Number(f.montoRestante ?? f.total),
                      0,
                    ),
                  )}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCerrar}>Cerrar</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={seleccionadas.length === 0}
          onClick={handlePagar}
          startIcon={<PaymentIcon />}
          className={classes.payButton}
        >
          {seleccionadas.length === 0
            ? "Selecciona facturas"
            : seleccionadas.length === 1
              ? "Pagar factura"
              : `Pagar ${seleccionadas.length} facturas`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
